/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { decorate, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';

import { createLink } from '../helpers/routing';

// Horizontal sub-navigation bar — sits below TopNav
// Supports dropdown groups via items with a `children` array
// Hides admin-only items when researcher view is active
// expected props
// - menuItems: array of { url, title, icon, shouldShow, adminOnly, children? }
// - userStore (via injection)
// - location (from react router)
// - history (from react router)
class SubNav extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.openDropdown = null;
    });
    this.closeTimer = null;
  }

  goto = pathname => () => {
    const location = this.props.location;
    const link = createLink({ location, pathname });
    this.props.history.push(link);
    runInAction(() => {
      this.openDropdown = null;
    });
  };

  handleDropdownEnter = title => {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    runInAction(() => {
      this.openDropdown = title;
    });
  };

  handleDropdownLeave = () => {
    this.closeTimer = setTimeout(() => {
      runInAction(() => {
        this.openDropdown = null;
      });
    }, 150);
  };

  render() {
    const items = this.props.menuItems || [];
    const pathname = _.get(this.props.location, 'pathname', '');
    const is = value => _.startsWith(pathname, value);
    const researcherView = this.props.userStore.researcherView;

    return (
      <nav className="rmit-sub-nav" id="main-layout-sub-nav">
        <div className="rmit-sub-nav-items">
          {_.map(items, (item, index) => {
            const show = (_.isFunction(item.shouldShow) && item.shouldShow()) || item.shouldShow;
            if (!show) return null;
            if (researcherView && item.adminOnly) return null;
            if (item.body) return <React.Fragment key={index}>{item.body}</React.Fragment>;

            // Dropdown group item
            if (item.children && item.children.length > 0) {
              const visibleChildren = _.filter(item.children, child => {
                const childShow = (_.isFunction(child.shouldShow) && child.shouldShow()) || child.shouldShow;
                if (!childShow) return false;
                if (researcherView && child.adminOnly) return false;
                return true;
              });
              if (visibleChildren.length === 0) return null;

              const childActive = _.some(visibleChildren, child => is(child.url));
              const isOpen = this.openDropdown === item.title;
              const isAdminGroup = item.adminOnly || _.every(visibleChildren, child => child.adminOnly);

              return (
                <div
                  key={index}
                  className={`rmit-sub-nav-dropdown${childActive ? ' active' : ''}${isAdminGroup ? ' admin-only' : ''}`}
                  onMouseEnter={() => this.handleDropdownEnter(item.title)}
                  onMouseLeave={this.handleDropdownLeave}
                >
                  <button
                    className={`rmit-sub-nav-item rmit-sub-nav-dropdown-trigger${childActive ? ' active' : ''}${isAdminGroup ? ' admin-only' : ''}`}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                  >
                    {item.title}
                    <Icon name="chevron down" style={{ marginLeft: 6, fontSize: '0.7em', opacity: 0.5 }} />
                  </button>
                  {isOpen && (
                    <div className="rmit-sub-nav-dropdown-menu">
                      {_.map(visibleChildren, (child, childIndex) => {
                        const childIsActive = is(child.url);
                        return (
                          <button
                            key={childIndex}
                            className={`rmit-sub-nav-dropdown-item${childIsActive ? ' active' : ''}`}
                            onClick={this.goto(child.url)}
                            aria-current={childIsActive ? 'page' : undefined}
                          >
                            {child.icon && <Icon name={child.icon} style={{ marginRight: 8 }} />}
                            {child.title}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Regular flat item
            const active = is(item.url);
            const exactMatch = pathname === item.url;
            return (
              <button
                key={index}
                className={`rmit-sub-nav-item${active ? ' active' : ''}${item.adminOnly ? ' admin-only' : ''}`}
                onClick={exactMatch ? undefined : this.goto(item.url)}
                aria-current={active ? 'page' : undefined}
              >
                {item.title}
              </button>
            );
          })}
        </div>
      </nav>
    );
  }
}

decorate(SubNav, {
  openDropdown: observable,
});

export default inject('userStore')(withRouter(observer(SubNav)));
